import { Selector } from '@casumo/dojo-react';
import { FC, useEffect } from 'react';
import useMarketInfo from '../../hooks/useMarketInfo.tsx';
import useNavigateTo from '../../hooks/useNavigateTo.tsx';
import { useRegistrationStore } from '../../store/store.ts';
import { TDepositLimitsField, TInputField } from '../../types/types.ts';
import { TEST_ID } from '../../utils/constants.ts';
import { GtmEvents } from '../../utils/gtm/gtm.ts';
import DepositLimits from '../depositLimits.tsx';

export enum DepositLimitOptInChoice {
  NO_LIMIT = 'no-limit',
  SET_LIMIT = 'set-limit',
}

type DepositLimitsWrapperProps = TDepositLimitsField & {
  onBlur?: (field: Partial<TInputField>) => void;
};

const DepositLimitsWrapper: FC<DepositLimitsWrapperProps> = ({
  requiresOptInConfirmation,
  noLimitOptionText,
  setLimitOptionText,
  ...props
}) => {
  const { market } = useMarketInfo();
  const { navigateTo } = useNavigateTo();
  const { metadata, setMetadata, setErrors, setFormData, setCustomValidations } = useRegistrationStore();

  const getCurrentStep = () => {
    const hash = location.hash;
    const match = hash.match(/register_(\d+)/);

    return match ? parseInt(match[1], 10) : 0;
  };

  const handleDepositLimitChoiceChange = (choice: DepositLimitOptInChoice) => {
    setMetadata({
      depositLimitChoice:  choice,
      depositLimitDefined: choice === DepositLimitOptInChoice.SET_LIMIT,
    });

    if (choice === DepositLimitOptInChoice.NO_LIMIT) {
      setFormData({
        daily:   '',
        weekly:  '',
        monthly: '',
      });

      setErrors({
        daily:   '',
        weekly:  '',
        monthly: '',
      })

      setMetadata({ depositType: undefined, limitType: undefined });

      GtmEvents.logDepositLimits(false);

      const nextStep = getCurrentStep() + 1;
      localStorage.setItem(`step_${market}`, nextStep.toString());
      navigateTo(market, nextStep);
    }
  };

  const shouldShowDepositLimitsForm = requiresOptInConfirmation
    ? metadata?.depositLimitChoice === DepositLimitOptInChoice.SET_LIMIT
    : true;

  useEffect(() => {
    setCustomValidations({
      daily:   '',
      weekly:  '',
      monthly: '',
    });
  }, [setCustomValidations])

  return (
    <>
      {requiresOptInConfirmation ? (
        <div className="flex flex-col gap-sm">
          <Selector.Radio
            className="text-center has-[:checked]:bg-purple-0 has-[:checked]:text-purple-80"
            value={DepositLimitOptInChoice.NO_LIMIT}
            name="deposit-limit-option"
            onClick={() => handleDepositLimitChoiceChange(DepositLimitOptInChoice.NO_LIMIT)}
            data-testid={TEST_ID.SELECT + DepositLimitOptInChoice.NO_LIMIT}
          >
            {noLimitOptionText}
          </Selector.Radio>
          <Selector.Radio
            className="text-center has-[:checked]:bg-purple-0 has-[:checked]:text-purple-80"
            value={DepositLimitOptInChoice.SET_LIMIT}
            name="deposit-limit-option"
            checked={metadata?.depositLimitChoice === DepositLimitOptInChoice.SET_LIMIT}
            onChange={() => handleDepositLimitChoiceChange(DepositLimitOptInChoice.SET_LIMIT)}
            data-testid={TEST_ID.SELECT + DepositLimitOptInChoice.SET_LIMIT}
          >
            {setLimitOptionText}
          </Selector.Radio>
        </div>
      ) : null}
      {shouldShowDepositLimitsForm && (
        <DepositLimits
          autoShowInput={!requiresOptInConfirmation}
          {...props}
        />
      )}
    </>
  );
};

export default DepositLimitsWrapper;
